import { Apis } from '../../app.constants.js';
import APIService from '../../APIService.js';

type AiActionsType = {
  sendChatGptQuery: (data: {
    query: string;
    ai_model: string;
    conversation_id?: string;
  }) => Promise<any>;
  getChatHistory: (data: any) => Promise<any>;
  getConversationList: (data: any) => Promise<any>;
  convertAudioToText: (audioFile: File, leadId: string, audioLink: string) => Promise<any>;
  getLeadTranscript: (leadId: string) => Promise<any>;
  getInstructionSet: (organisationId: string) => Promise<any>;
  updateInstructionSet: (data) => Promise<any>;
  postInstructionSet: (data) => Promise<any>;
  createIndex: (data) => Promise<any>;
  addVector: (data) => Promise<any>;
  updateVector: (data) => Promise<any>;
  deleteVector: (data) => Promise<any>;
  createProject: (data) => Promise<any>;
  getProjectList: (data) => Promise<any>;

  getBenchmarkReport: (params: { project_id?: string; conversation_id?: string }) => Promise<any>;
  downloadBenchmarkReport: (params: {
    project_id?: string;
    conversation_id?: string;
  }) => Promise<any>;
  getBenchmarkSummary: (params: { project_id?: string; conversation_id?: string }) => Promise<any>;
  getBenchmarkModelWise: (params: {
    project_id?: string;
    conversation_id?: string;
  }) => Promise<any>;
  getBenchmarkConversationWise: (params: {
    project_id?: string;
    conversation_id?: string;
  }) => Promise<any>;
  getBenchmarkComparisonMatrix: (params: {
    project_id?: string;
    conversation_id?: string;
  }) => Promise<any>;

  createKnowledgeBase: (data: any) => Promise<any>;
  listKnowledgeBases: (orgId: string) => Promise<any>;
  getKnowledgeBaseDetails: (
    orgId: string,
    kbId: string,
    fileId: string,
    includeChunks?: boolean
  ) => Promise<any>;
  updateKnowledgeBase: (orgId: string, kbId: string, data: any) => Promise<any>;
  deleteKnowledgeBase: (orgId: string, kbId: string) => Promise<any>;
  getFileList: (organization_id: string, knowledge_base_id: string) => Promise<any>;
  uploadFile: (
    organization_id: string,
    knowledge_base_id: string,
    formData: FormData
  ) => Promise<any>;
  getFileDetails: (
    organization_id: string,
    knowledge_base_id: string,
    file_id: string
  ) => Promise<any>;
  searchKnowledgeBase: (
    organization_id: string,
    knowledge_base_id: string,
    data: any
  ) => Promise<any>;
  searchMultiKnowledgeBase: (organization_id: string, data: any) => Promise<any>;
  retrieveChunks: (organization_id: string, data: any) => Promise<any>;
  getSimilarChunks: (
    organization_id: string,
    knowledge_base_id: string,
    chunk_id: string,
    data?: any
  ) => Promise<any>;
  getOrganizationSearchStats: (organization_id: string) => Promise<any>;
  deleteFile: (organization_id: string, knowledge_base_id: string, file_id: string) => Promise<any>;
  getKnowledgeList: () => Promise<any>;
  getStudentPerformanceWithKnowledgeMap: (user_id: string) => Promise<any>; // ADD THIS LINE
  getTestData: (data: {
    class_group: string;
    subject?: string;
    topic?: string;
    subtopic?: string;
    user_id?: string;
    test_id?: string;
    campaign_id?: string;
    test_submitted?: boolean;
  }) => Promise<any>;
  getTestData: (data: {
    class_group: string;
    subject?: string;
    topic?: string;
    subtopic?: string;
    user_id?: string;
    test_id?: string;
    campaign_id?: string;
    test_submitted?: boolean;
  }) => Promise<any>;
  getTestPaper: (data: {
    class?: string;
    test_type?: string;
    test_started?: boolean;
    check_submitted?: string;
    subject?: string;
    // page?: number;
    // page_size?: number;
  }) => Promise<any>;
  generateAndStoreQuestions: (data: any) => Promise<any>;
  getQuestions: (params: {
    board?: string;
    class?: string;
    subject?: string;
    topic?: string;
    subtopic?: string;
    difficulty?: string;
    question_type?: string;
    campaign_id?: string;
    lead_form_id?: string;
    user_id?: string;
    phone_number?: string;
    test_id?: string;
  }) => Promise<any>;
  verifyQuestions: (data: any) => Promise<any>;
  verifyMe: (data: any) => Promise<any>;
  deleteQuestions: (
    payload: string[] | { question_ids?: string[]; question_bank_id?: string }
  ) => Promise<any>;
  getOrganizationInstruction: (organization_id: string) => Promise<any>;
  updateOrganizationInstruction: (data: any) => Promise<any>;
  createOrganizationInstruction: (data: any) => Promise<any>;
  organizationChat: (data: any) => Promise<any>;
};

const AiActions = (): AiActionsType => {
  const sendChatGptQuery = async (data: {
    query: string;
    ai_model: string;
    conversation_id?: string;
  }) => {
    try {
      const res = await APIService.post('v0/ui/whatsapp-v2/chatgpt-key/', data);
      return res;
    } catch (error) {
      throw error;
    }
  };

  const getChatHistory = async (data?: any) => {
    try {
      let url = 'v0/ui/whatsapp-v2/chatgpt-key/';
      if (data?.ai_model) {
        url += `?ai_model=${encodeURIComponent(data?.ai_model)}`;
      }
      if (data?.conversation_id) {
        url += `&conversation_id=${encodeURIComponent(data?.conversation_id)}`;
      }
      const res = await APIService.get(url);
      return res;
    } catch (error) {
      console.error('Error fetching chat history:', error);
      throw error;
    }
  };

  const getConversationList = async (data: any) => {
    try {
      let url = 'v0/ui/whatsapp-v2/conversation-list/';
      const queryParams = new URLSearchParams();

      if (data?.organisation_id) {
        queryParams.append('organisation_id', data?.organisation_id);
      }
      if (data?.campaign_id) {
        queryParams.append('campaign_id', data?.campaign_id);
      }
      if (data?.user_id) {
        queryParams.append('user_id', data?.user_id);
      }
      if (data?.project_id) {
        queryParams.append('project_id', data?.project_id);
      }

      // ✅ ADD THESE NEW QUERY PARAMS
      if (data?.phone_number) {
        queryParams.append('phone_number', data?.phone_number);
      }
      // if (data?.subject) {
      //   queryParams.append('subject', data?.subject);
      // }
      // if (data?.topic) {
      //   queryParams.append('topic', data?.topic);
      // }

      if (data?.parameters && Object.keys(data.parameters).length > 0) {
        queryParams.append('parameters', JSON.stringify(data.parameters));
      }
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      const res = await APIService.get(url);
      return res;
    } catch (error) {
      console.error('❌ Error in getConversationList:', error);
      throw error;
    }
  };
  const convertAudioToText = async (
    audioFile: File,
    leadId: string,
    audioLink: string
  ): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append('audio', audioFile);
      formData.append('audio_link', audioLink);

      const url = `v0/ui/ai-agent/audio-to-text/?lead_id=${encodeURIComponent(leadId)}`;
      const response = await APIService.uploadFile(url, formData);
      const result = response.data;

      return result;
    } catch (error) {
      console.error('❌ Error converting audio to text:', error);
      throw error;
    }
  };

  const getLeadTranscript = async (leadId: string): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/get-lead-transcript/?lead_id=${encodeURIComponent(leadId)}`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching lead transcript:', error);
      throw error;
    }
  };

  const uploadAudion = async (leadId: string): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/audio-to-text/?lead_id=${encodeURIComponent(leadId)}`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching lead transcript:', error);
      throw error;
    }
  };

  const getInstructionSet = async (data: any): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/instruction-set/?organisation_id=${encodeURIComponent(data?.organisation_id ?? '')}&campaign_id=${encodeURIComponent(data?.campaign_id ?? '')}`;
      const response = await APIService.get(url);

      if (!response || response?.status === false || response?.data?.general_error) {
        throw new Error(response?.data?.general_error || 'Instruction not found.');
      }

      return response.data;
    } catch (error) {
      console.error('❌ Error in getInstructionSet:', error);
      throw error;
    }
  };

  const updateInstructionSet = async (data: any): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/instruction-set/?organisation_id=${encodeURIComponent(data?.organisation_id ?? '')}&campaign_id=${encodeURIComponent(data?.campaign_id ?? '')}`;

      // const payload = {
      //   organisation_id: organisationId,
      //   instruction: instruction,
      // };

      const response = await APIService.put(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating instruction set:', error);
      throw error;
    }
  };

  const postInstructionSet = async (data): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/instruction-set/?organisation_id=${encodeURIComponent(data?.organisation_id ?? '')}&campaign_id=${encodeURIComponent(data?.campaign_id ?? '')}`;
      // const payload = {
      //   organisation_id: organisationId,
      //   instruction: instruction,
      // };
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating instruction set:', error);
      throw error;
    }
  };

  const createIndex = async (data: any) => {
    try {
      const res = await APIService.post('v0/ui/vector/index/', data);
      return res;
    } catch (error) {
      console.error('Error creating index:', error);
      throw error;
    }
  };

  const addVector = async (data: any) => {
    try {
      return await APIService.post('/vector/vectors/add/', data);
    } catch (error) {
      console.error('Error adding vector:', error);
      throw error;
    }
  };

  const updateVector = async (data: any) => {
    try {
      return await APIService.put('/vector/vectors/add/', data);
    } catch (error) {
      console.error('Error updating vector:', error);
      throw error;
    }
  };

  const deleteVector = async (data: { vector_id: string }) => {
    try {
      return await APIService.delete('/ai-agent/vectors/add/', { data });
    } catch (error) {
      console.error('Error deleting vector:', error);
      throw error;
    }
  };

  const createProject = async (data: any) => {
    try {
      const response = await APIService.post(`${Apis.createProject}`, data);
      return response;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  };
  const getProjectList = async (data: any) => {
    try {
      const response = await APIService.get(
        `${Apis.createProject}?organisation_id=${data?.organisation_id}&campaign_id=${data?.campaign_id}`
      );
      return response;
    } catch (error) {
      console.error('Error getting project list:', error);
      throw error;
    }
  };

  const getBenchmarkReport = async (params: { project_id?: string; conversation_id?: string }) => {
    const query = new URLSearchParams();
    if (params.project_id) query.append('project_id', params.project_id);
    if (params.conversation_id) query.append('conversation_id', params.conversation_id);
    const url = `/v0/ui/ai-agent/benchmark-report/?${query.toString()}`;
    const response = await APIService.get(url);
    return response;
  };

  const downloadBenchmarkReport = (params: { project_id?: string; conversation_id?: string }) => {
    try {
      const query = new URLSearchParams();
      if (params.conversation_id) query.append('conversation_id', params.conversation_id);
      if (params.project_id) query.append('project_id', params.project_id);

      const url = `${process.env.REACT_APP_API_URL}/v0/ui/ai-agent/download-benchmark-report/?${query.toString()}`;
      window.location.href = url;
    } catch (error) {
      throw new Error(error as string);
    }
  };

  const getBenchmarkSummary = async (params: { project_id?: string; conversation_id?: string }) => {
    const query = new URLSearchParams();
    if (params.project_id) query.append('project_id', params.project_id);
    if (params.conversation_id) query.append('conversation_id', params.conversation_id);
    const url = `/v0/ui/ai-agent/benchmark/summary/?${query.toString()}`;
    const response = await APIService.get(url);
    return response;
  };

  const getBenchmarkModelWise = async (params: {
    project_id?: string;
    conversation_id?: string;
  }) => {
    const query = new URLSearchParams();
    if (params.project_id) query.append('project_id', params.project_id);
    if (params.conversation_id) query.append('conversation_id', params.conversation_id);
    const url = `/v0/ui/ai-agent/benchmark/model-wise/?${query.toString()}`;
    const response = await APIService.get(url);
    return response;
  };

  const getBenchmarkConversationWise = async (params: {
    project_id?: string;
    conversation_id?: string;
  }) => {
    const query = new URLSearchParams();
    if (params.project_id) query.append('project_id', params.project_id);
    if (params.conversation_id) query.append('conversation_id', params.conversation_id);
    const url = `/v0/ui/ai-agent/benchmark/conversation-wise/?${query.toString()}`;
    const response = await APIService.get(url);
    return response;
  };

  const getBenchmarkComparisonMatrix = async (params: {
    project_id?: string;
    conversation_id?: string;
  }) => {
    const query = new URLSearchParams();
    if (params.project_id) query.append('project_id', params.project_id);
    if (params.conversation_id) query.append('conversation_id', params.conversation_id);
    const url = `/v0/ui/ai-agent/benchmark/comparison-matrix/?${query.toString()}`;
    const response = await APIService.get(url);
    return response;
  };

  const createKnowledgeBase = async (data: any): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${data.organization_id}/knowledge-bases/`;
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating knowledge base:', error);
      throw error;
    }
  };

  const listKnowledgeBases = async (orgId: string): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${orgId}/knowledge-bases/`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching knowledge base list:', error);
      throw error;
    }
  };

  const getKnowledgeBaseDetails = async (
    orgId: string,
    kbId: string,
    fileId: string,
    includeChunks: boolean = false
  ): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${orgId}/knowledge-bases/${kbId}/?file_id=${fileId}&include_chunks=${includeChunks}`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting knowledge base details:', error);
      throw error;
    }
  };

  const updateKnowledgeBase = async (orgId: string, kbId: string, data: any): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${orgId}/knowledge-bases/${kbId}/`;
      const response = await APIService.put(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating knowledge base:', error);
      throw error;
    }
  };

  const deleteKnowledgeBase = async (orgId: string, kbId: string): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${orgId}/knowledge-bases/${kbId}/`;
      const response = await APIService.remove(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting knowledge base:', error);
      throw error;
    }
  };

  const getFileList = async (organization_id: string, knowledge_base_id: string): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/knowledge-bases/${knowledge_base_id}/files/`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching file list:', error);
      throw error;
    }
  };

  const uploadFile = async (
    organization_id: string,
    knowledge_base_id: string,
    formData: FormData
  ): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/knowledge-bases/${knowledge_base_id}/files/upload/`;
      const response = await APIService.uploadFile(url, formData);
      return response.data;
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      throw error;
    }
  };

  const getFileDetails = async (
    organization_id: string,
    knowledge_base_id: string,
    file_id: string,
    include_chunks: boolean = true
  ): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/knowledge-bases/${knowledge_base_id}/files/${file_id}/?include_chunks=${include_chunks}`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching file details:', error);
      throw error;
    }
  };

  const searchKnowledgeBase = async (
    organization_id: string,
    knowledge_base_id: string,
    data: any
  ): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/knowledge-bases/${knowledge_base_id}/search/`;
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error searching knowledge base:', error);
      throw error;
    }
  };

  const searchMultiKnowledgeBase = async (organization_id: string, data: any): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/search/`;
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error searching multiple knowledge bases:', error);
      throw error;
    }
  };

  const retrieveChunks = async (organization_id: string, data: any): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/chunks/retrieve/`;
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error retrieving chunks:', error);
      throw error;
    }
  };

  const getSimilarChunks = async (
    organization_id: string,
    knowledge_base_id: string,
    chunk_id: string,
    data: any = {}
  ): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/knowledge-bases/${knowledge_base_id}/chunks/${chunk_id}/similar/`;
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching similar chunks:', error);
      throw error;
    }
  };

  const getOrganizationSearchStats = async (organization_id: string): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/search/stats/`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching organization search stats:', error);
      throw error;
    }
  };

  const deleteFile = async (
    organization_id: string,
    knowledge_base_id: string,
    file_id: string
  ): Promise<any> => {
    try {
      const url = `v0/ui/vector/organizations/${organization_id}/knowledge-bases/${knowledge_base_id}/files/${file_id}/`;
      const response = await APIService.remove(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting file:', error);
      throw error;
    }
  };
  const getTestApi = async (queryParams: { testId: string; showAnswers: number }): Promise<any> => {
    try {
      const { testId, showAnswers } = queryParams;
      let url = `v0/ui/leads/get-test-api/?test_id=${encodeURIComponent(testId)}`;
      url += `&show_answers=${showAnswers ?? 0}`;
      const response = await APIService.get(url);
      return response;
    } catch (error) {
      console.error('❌ Error fetching test API:', error);
      throw error;
    }
  };

  const startTestApi = async (data: { test_id: string }): Promise<any> => {
    try {
      const url = 'v0/ui/leads/start-test-api/';
      const response = await APIService.post(url, data);
      return response;
    } catch (error) {
      console.error('❌ Error starting test:', error);
      throw error;
    }
  };

  const submitTestApi = async (data: {
    test_id: string;
    responses: Record<string, string>;
    time_taken: number;
  }): Promise<any> => {
    try {
      const url = 'v0/ui/leads/submit-test-api/';
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error submitting test:', error);
      throw error;
    }
  };

  const getKnowledgeList = async (organisationId?: string): Promise<any> => {
    try {
      // const url = `${Apis.knowledgeList}`;
      const url = organisationId
        ? `${Apis.knowledgeList}?organisation_id=${organisationId}`
        : `${Apis.knowledgeList}`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching knowledge list:', error);
      throw error;
    }
  };

  const getKnowledgeById = async (kbId: string): Promise<any> => {
    try {
      const url = `${Apis.knowledgeList}${kbId}/`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching knowledge record:', error);
      throw error;
    }
  };

  const createKnowledge = async (data: any): Promise<any> => {
    try {
      const url = `${Apis.knowledgeList}`;
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating knowledge record:', error);
      throw error;
    }
  };

  const updateKnowledge = async (kbId: string, data: any): Promise<any> => {
    try {
      const url = `${Apis.knowledgeList}${kbId}/`;
      const response = await APIService.put(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating knowledge record:', error);
      throw error;
    }
  };

  const deleteKnowledge = async (kbId: string): Promise<any> => {
    try {
      const url = `${Apis.knowledgeList}${kbId}/`;
      const response = await APIService.remove(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting knowledge record:', error);
      throw error;
    }
  };

  const getTestDoubtData = async (data: any): Promise<any> => {
    try {
      const url = `${Apis.testDoubtData}/`;

      const response = await APIService.post(url, data);

      return response.data;
    } catch (error) {
      console.error('❌ Error fetching Data:', error);
      throw error;
    }
  };

  const getPracticeTest = async (data: any): Promise<any> => {
    try {
      const url = `${Apis.practiceTest}`;

      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching Data:', error);
      throw error;
    }
  };
  const getImageUrl = async (data: any): Promise<any> => {
    try {
      const url = `${Apis.imageUrl}`;

      const response = await APIService.post(url, data);

      return response.data;
    } catch (error) {
      console.error('❌ Error fetching Data:', error);
      throw error;
    }
  };
  //    const getTestDoubtData = async (data: any): Promise<any> => {
  //   try {
  //     let url = `${Apis.testDoubtData}/`;
  //     const queryParams = new URLSearchParams();

  //     // ✅ ONLY THESE 3 IN QUERY PARAMS
  //     if (data?.subject) {
  //       queryParams.append('subject', data.subject);
  //     }
  //     if (data?.topic) {
  //       queryParams.append('topic', data.topic);
  //     }
  //     if (data?.subtopic) {
  //       queryParams.append('subtopic', data.subtopic);
  //     }

  //     // Build URL with query params
  //     const queryString = queryParams.toString();
  //     if (queryString) {
  //       url += `?${queryString}`;
  //     }

  //     // ✅ REMOVE subject/topic/subtopic FROM BODY, SEND EVERYTHING ELSE
  //     const { subject, topic, subtopic, ...bodyData } = data;
  //     const response = await APIService.post(url, bodyData);

  //     return response.data;
  //   } catch (error) {
  //     console.error('❌ Error fetching Data:', error);
  //     throw error;
  //   }
  // };

  const getStudentPerformance = async (
    user_id: string,
    phone_number: string
  ): Promise<any> => {
    try {
      const url = `v0/ui/school/get-student-performance/?user_id=${encodeURIComponent(
        user_id
      )}&phone_number=${encodeURIComponent(phone_number)}`;

      const response = await APIService.get(url);
      return response;
    } catch (error) {
      console.error("❌ Error fetching student performance:", error);
      throw error;
    }
  };

  const getRecordEntity = async (
    phoneNumber: string,
    params?: Record<string, any>
  ) => {
    const queryParams = new URLSearchParams({
      phone_number: phoneNumber,
      ...(params || {}),   // template_form_type will come from here
    }).toString();

    try {
      const url = `v0/ui/entity-management/get-records-by-form-type?${queryParams}`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching file list:', error);
      throw error;
    }
  };


  const getStudentPerformanceWithKnowledgeMap = async (
    user_id: string
  ): Promise<any> => {
    try {
      const url = `v0/ui/school/get-student-performance-with-knowledge-map/?user_id=${encodeURIComponent(
        user_id
      )}`;

      const response = await APIService.get(url);
      return response;
    } catch (error) {
      console.error("❌ Error fetching student performance with knowledge map:", error);
      throw error;
    }
  };

  const getTeacherReport = async (class_group: string, subject: string): Promise<any> => {
    try {
      const url = `v0/ui/school/get-teacher-report/?class_group=${encodeURIComponent(
        class_group
      )}&subject=${encodeURIComponent(subject)}`;

      const response = await APIService.get(url);
      return response;
    } catch (error) {
      console.error('❌ Error fetching teacher report:', error);
      throw error;
    }
  };

  const getTestData = async (data: {
    class_group: string;
    subject?: string;
    topic?: string;
    subtopic?: string;
    user_id?: string;
    test_id?: string;
    campaign_id?: string;
    test_submitted?: boolean;
  }): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();

      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      const url = `v0/ui/school/get-test-data/?${queryParams.toString()}`;
      return await APIService.get(url);
    } catch (error) {
      console.error('❌ Error fetching test data:', error);
      throw error;
    }
  };

  const getTestPaper = async (data: {
    class?: string;
    test_type?: string;
    test_started?: boolean;
    check_submitted?: string;
    subject?: string;
    // page?: number;
    // page_size?: number;
  }): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();

      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      const url = `v0/ui/school/get-test-paper/?${queryParams.toString()}`;
      return await APIService.get(url);
    } catch (error) {
      console.error('❌ Error fetching test paper:', error);
      throw error;
    }
  };

  const generateAndStoreQuestions = async (data: any): Promise<any> => {
    try {
      const url = '/v0/ui/school/questions-bank/';
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error generating and storing questions:', error);
      throw error;
    }
  };
  const getQuestions = async (params: {
    board?: string;
    class?: string;
    subject?: string;
    topic?: string;
    subtopic?: string;
    difficulty?: string;
    question_type?: string;
    campaign_id?: string;
    lead_form_id?: string;
    user_id?: string;
    phone_number?: string;
    test_id?: string;
    limit?: number;
    offset?: number;
    difficulty_count?: string;
  }): Promise<any> => {
    try {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
      const url = `/v0/ui/school/questions-bank/?${queryParams.toString()}`;
      const response = await APIService.get(url);
      // return response?.data;
      return response;
    } catch (error) {
      console.error('❌ Error fetching questions:', error);
      throw error;
    }
  };

  const verifyQuestions = async (data: any): Promise<any> => {
    try {
      const url = '/v0/ui/school/verify-questions-async/';
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error generating and storing questions:', error);
      throw error;
    }
  };

  const verifyMe = async (data: any): Promise<any> => {
    try {
      const url = `${Apis.questionBank}`;
      const response = await APIService.put(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating knowledge base:', error);
      throw error;
    }
  };

  const deleteQuestions = async (
    payload: string[] | { question_ids?: string[]; question_bank_id?: string }
  ): Promise<any> => {
    try {
      const url = `${Apis.questionBank}`; const deletePayload = Array.isArray(payload) ? { question_ids: payload } : payload;
      const response = await APIService.remove(url, deletePayload);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting questions:', error);
      throw error;
    }
  };


  const sendDoubtWithImages = async (formData: FormData): Promise<any> => {
    try {
      const url = '/v0/ui/school/get-images-test-doubt-data/';
      const response = await APIService.uploadFile(url, formData);
      return response;
    } catch (error) {
      console.error('❌ Error sending doubt with images:', error);
      throw error;
    }
  };

  const createQuestionBank = async (data: any) => {
    try {
      const res = await APIService.post('v0/ui/school/questions-bank/', data);
      return res;
    } catch (error) {
      console.error('Error creating index:', error);
      throw error;
    }
  };

  const updateQuestions = async (data: any): Promise<any> => {
    try {
      const url = `${Apis.questionBank}`;
      const response = await APIService.put(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating knowledge record:', error);
      throw error;
    }
  };

  const bulkUploadQuestions = async (formData: FormData
  ): Promise<any> => {
    try {
      const url = `v0/ui/school/generate-test-from-excel/`;
      const response = await APIService.uploadFile(url, formData);
      return response.data;
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      throw error;
    }
  };

  const downloadsamplequesionExcel = async (): Promise<any> => {
    try {
      const url = `${Apis.downloadTestExcelSample}`;
      await APIService.downloadFile(url, 'sample_questions.xlsx');
    } catch (error) {
      console.error('❌ Error downloading sample question Excel:', error);
      throw error;
    }
  };

  const exportQuestions = async (OrgId:string): Promise<any> => {
    try {
      const url = `${Apis.exportQuestions}?org_id=${OrgId}`;
      await APIService.downloadFile(url);
    } catch (error) {
      console.error('❌ Error downloading sample question Excel:', error);
      throw error;
    }
  };

  const getOrganizationInstruction = async (organization_id: string): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/organization-instruction?organization_id=${encodeURIComponent(
        organization_id
      )}`;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching organization instruction:', error);
      throw error;
    }
  };

  const updateOrganizationInstruction = async (data: any): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/organization-instruction`;
      const response = await APIService.put(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating organization instruction:', error);
      throw error;
    }
  };

  const createOrganizationInstruction = async (data: any): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/organization-instruction`;
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating organization instruction:', error);
      throw error;
    }
  };

  // const organizationChat = async (data: {
  //   organization_id: string;
  //   query: string;
  //   type: string;
  // }): Promise<any> => {
  //   try {
  //     const url = `v0/ui/ai-agent/organization-chat`;
  //     const response = await APIService.post(url, data);
  //     return response.data;
  //   } catch (error) {
  //     console.error('❌ Error in organizationChat:', error);
  //     throw error;
  //   }
  // };

  const organizationChat = async (data: FormData | Record<string, any>): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/organization-chat`;
      const response = await APIService.uploadFile(url, data);
      return response.data;
    }
    catch (error) {
      console.error('❌ Error in organizationChat:', error);
      throw error;
    }
  };

  const presentation = async (data: {
    organization_id: string;
    prompt: string;
  }): Promise<any> => {
    try {
      const url = `v0/ui/ai-agent/ppt-generation/`;
      const response = await APIService.post(url, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error in organizationChat:', error);
      throw error;
    }
  };


  // ==========================================
  // Curriculum Orchestrator API Actions
  // ==========================================
  const getSyllabusList = async (knowledge_map_id?: string): Promise<any> => {
    try {
      const url = knowledge_map_id
        ? `${Apis.syllabusList}?knowledge_map_id=${knowledge_map_id}`
        : Apis.syllabusList;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching syllabus list:', error);
      throw error;
    }
  };

  const getSyllabusById = async (id: string): Promise<any> => {
    try {
      const response = await APIService.get(`${Apis.syllabusDetail}${id}/`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching syllabus:', error);
      throw error;
    }
  };

  const createSyllabus = async (data: Record<string, unknown>): Promise<any> => {
    try {
      const response = await APIService.post(Apis.syllabusList, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating syllabus:', error);
      throw error;
    }
  };

  const updateSyllabus = async (id: string, data: Record<string, unknown>): Promise<any> => {
    try {
      const response = await APIService.put(`${Apis.syllabusDetail}${id}/`, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating syllabus:', error);
      throw error;
    }
  };

  const createStudyPlan = async (data: Record<string, unknown>): Promise<any> => {
    try {
      const response = await APIService.post(Apis.studyPlanList, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating study plan:', error);
      throw error;
    }
  };

  const getStudyPlan = async (syllabus_id?: string): Promise<any> => {
    try {
      const url = syllabus_id
        ? `${Apis.studyPlanList}?syllabus_id=${syllabus_id}`
        : Apis.studyPlanList;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching study plan:', error);
      throw error;
    }
  };

  const getStudyPlanById = async (id: string): Promise<any> => {
    try {
      const response = await APIService.get(`${Apis.studyPlanDetail}${id}/`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching study plan:', error);
      throw error;
    }
  };

  const updateStudyPlan = async (plan_id: string, data: Record<string, unknown>): Promise<any> => {
    try {
      const response = await APIService.put(`${Apis.studyPlanDetail}${plan_id}/`, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating study plan:', error);
      throw error;
    }
  };

  const getTeacherNotes = async (study_plan_id?: string): Promise<any> => {
    try {
      const url = study_plan_id
        ? `${Apis.teacherNotesList}?study_plan_id=${study_plan_id}`
        : Apis.teacherNotesList;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching teacher notes:', error);
      throw error;
    }
  };

  const getTeacherNotesById = async (id: string): Promise<any> => {
    try {
      const response = await APIService.get(`${Apis.teacherNotesDetail}${id}/`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching teacher notes:', error);
      throw error;
    }
  };

  const getStudentDeliverables = async (
    teacher_notes_id?: string,
    lecture_id?: string
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      if (teacher_notes_id) params.set('teacher_notes_id', teacher_notes_id);
      if (lecture_id) params.set('lecture_id', lecture_id);
      const qs = params.toString();
      const url = qs ? `${Apis.studentDeliverablesList}?${qs}` : Apis.studentDeliverablesList;
      const response = await APIService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching student deliverables:', error);
      throw error;
    }
  };

  const getStudentDeliverableById = async (id: string): Promise<any> => {
    try {
      const response = await APIService.get(`${Apis.studentDeliverablesDetail}${id}/`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching student deliverable:', error);
      throw error;
    }
  };

  const evaluateAnswer = async (data: {
    question_text: string;
    student_answer: string;
    reference_answer: string;
    topic?: string;
  }): Promise<any> => {
    try {
      const response = await APIService.post(Apis.aiEvaluation, data);
      return response.data;
    } catch (error) {
      console.error('❌ Error evaluating answer:', error);
      throw error;
    }
  };

  const generateStudyPlanWithAI = async (syllabus_id: string): Promise<any> => {
    try {
      const response = await APIService.post(Apis.studyPlanAiGenerate, { syllabus_id });
      return response.data;
    } catch (error) {
      console.error('❌ Error triggering AI study plan generation:', error);
      throw error;
    }
  };

  const generateTeacherGuidelinesWithAI = async (study_plan_id: string): Promise<any> => {
    try {
      const response = await APIService.post(Apis.teacherNotesAiGenerate, { study_plan_id });
      return response.data;
    } catch (error) {
      console.error('❌ Error triggering AI teacher guidelines generation:', error);
      throw error;
    }
  };

  const getAIGenerationStatus = async (study_plan_id: string): Promise<any> => {
    try {
      const response = await APIService.get(
        `${Apis.aiGenerationStatus}?study_plan_id=${study_plan_id}`
      );
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching AI generation status:', error);
      throw error;
    }
  };

  return {
    sendChatGptQuery,
    getChatHistory,
    getConversationList,
    convertAudioToText,
    getLeadTranscript,
    getInstructionSet,
    updateInstructionSet,
    postInstructionSet,
    createIndex,
    addVector,
    updateVector,
    deleteVector,
    createProject,
    getProjectList,
    downloadBenchmarkReport,
    getBenchmarkReport,
    getBenchmarkSummary,
    getBenchmarkModelWise,
    getBenchmarkConversationWise,
    getBenchmarkComparisonMatrix,
    createKnowledgeBase,
    listKnowledgeBases,
    getKnowledgeBaseDetails,
    updateKnowledgeBase,
    deleteKnowledgeBase,
    getFileList,
    uploadFile,
    getFileDetails,
    searchKnowledgeBase,
    searchMultiKnowledgeBase,
    retrieveChunks,
    getSimilarChunks,
    getOrganizationSearchStats,
    deleteFile,
    startTestApi,
    submitTestApi,
    getTestApi,
    getKnowledgeList,
    getKnowledgeById,
    createKnowledge,
    updateKnowledge,
    deleteKnowledge,
    getTestDoubtData,
    getStudentPerformance,
    getRecordEntity,
    getStudentPerformanceWithKnowledgeMap,
    getTeacherReport,
    getTestData,
    getTestPaper,
    generateAndStoreQuestions,
    getQuestions,
    getImageUrl,
    verifyQuestions,
    verifyMe,
    deleteQuestions,
    getPracticeTest,
    sendDoubtWithImages,
    createQuestionBank,
    updateQuestions,
    bulkUploadQuestions,
    downloadsamplequesionExcel,
    exportQuestions,
    getOrganizationInstruction,
    updateOrganizationInstruction,
    createOrganizationInstruction,
    organizationChat,
    // Curriculum Endpoints
    getSyllabusList,
    getSyllabusById,
    createSyllabus,
    updateSyllabus,
    createStudyPlan,
    getStudyPlan,
    getStudyPlanById,
    updateStudyPlan,
    getTeacherNotes,
    getTeacherNotesById,
    getStudentDeliverables,
    getStudentDeliverableById,
    evaluateAnswer,
    generateStudyPlanWithAI,
    generateTeacherGuidelinesWithAI,
    getAIGenerationStatus,
  };
};

export { AiActions };
